import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic admin CRUD manager for categories, testimonials, faqs, banners
 */
export function CrudManager({ title, resource, columns, defaults, fields, testId }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/${resource}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [resource]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/${resource}/${editing.id}`, editing);
      } else {
        await api.post(`/${resource}`, editing);
      }
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/${resource}/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="kyro-overline">Content</div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mt-2">{title}</h1>
        </div>
        <button onClick={() => setEditing({ ...defaults })} className="kyro-btn-primary text-sm inline-flex items-center gap-1" data-testid={`${testId}-new-btn`}><Plus className="w-4 h-4" /> New</button>
      </div>

      <div className="kyro-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left">
              {columns.map((c) => <th key={c.key} className="px-4 py-3 font-medium text-kyro-mute">{c.label}</th>)}
              <th className="px-4 py-3 w-0"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-white/5">
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-3">
                    {c.render ? c.render(it) : (typeof it[c.key] === "string" ? it[c.key].slice(0, 80) : String(it[c.key] ?? ""))}
                  </td>
                ))}
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing({ ...it })} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(it.id)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-white/5 text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
          <div className="kyro-card w-full max-w-2xl p-6 md:p-8 my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-bold text-2xl">{editing.id ? "Edit" : "New"} {title}</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fields.map((f) => {
                if (f.type === "textarea") return (
                  <textarea key={f.key} placeholder={f.label} rows={3} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                );
                if (f.type === "checkbox") return (
                  <label key={f.key} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} className="accent-[#FF4433]" /> {f.label}
                  </label>
                );
                if (f.type === "number") return (
                  <input key={f.key} type="number" placeholder={f.label} value={editing[f.key] ?? 0} onChange={(e) => setEditing({ ...editing, [f.key]: parseFloat(e.target.value || "0") })} className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]" />
                );
                return (
                  <input key={f.key} placeholder={f.label} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} className={`${f.full ? "md:col-span-2 " : ""}bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 outline-none focus:border-[#FF4433]`} />
                );
              })}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="kyro-btn-secondary text-sm">Cancel</button>
              <button onClick={save} className="kyro-btn-primary text-sm" data-testid={`${testId}-save-btn`}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCategories() {
  return <CrudManager
    title="Categories" resource="categories" testId="admin-cat"
    defaults={{ slug: "", name: "", tagline: "", icon: "", sort_order: 0, is_active: true }}
    columns={[
      { key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "tagline", label: "Tagline" },
      { key: "sort_order", label: "Order" }, { key: "is_active", label: "Active", render: (i) => i.is_active ? "Yes" : "No" },
    ]}
    fields={[
      { key: "name", label: "Name" }, { key: "slug", label: "Slug" }, { key: "tagline", label: "Tagline", full: true },
      { key: "icon", label: "Icon" }, { key: "sort_order", label: "Sort order", type: "number" }, { key: "is_active", label: "Active", type: "checkbox" },
    ]}
  />;
}

export function AdminTestimonials() {
  return <CrudManager
    title="Testimonials" resource="testimonials" testId="admin-tst"
    defaults={{ name: "", role: "", quote: "", rating: 5, is_active: true, sort_order: 0 }}
    columns={[
      { key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "quote", label: "Quote" },
      { key: "rating", label: "Rating" }, { key: "is_active", label: "Active", render: (i) => i.is_active ? "Yes" : "No" },
    ]}
    fields={[
      { key: "name", label: "Name" }, { key: "role", label: "Role" },
      { key: "quote", label: "Quote", type: "textarea" },
      { key: "rating", label: "Rating (1-5)", type: "number" },
      { key: "sort_order", label: "Order", type: "number" }, { key: "is_active", label: "Active", type: "checkbox" },
    ]}
  />;
}

export function AdminFaqs() {
  return <CrudManager
    title="FAQs" resource="faqs" testId="admin-faq"
    defaults={{ question: "", answer: "", category: "general", sort_order: 0, is_active: true }}
    columns={[
      { key: "question", label: "Question" }, { key: "category", label: "Category" },
      { key: "sort_order", label: "Order" }, { key: "is_active", label: "Active", render: (i) => i.is_active ? "Yes" : "No" },
    ]}
    fields={[
      { key: "question", label: "Question", full: true },
      { key: "answer", label: "Answer", type: "textarea" },
      { key: "category", label: "Category" }, { key: "sort_order", label: "Order", type: "number" },
      { key: "is_active", label: "Active", type: "checkbox" },
    ]}
  />;
}

export function AdminBanners() {
  return <CrudManager
    title="Banners" resource="banners" testId="admin-bnr"
    defaults={{ title: "", subtitle: "", cta_label: "", cta_link: "", image_url: "", accent_color: "#FF4433", is_active: true, sort_order: 0 }}
    columns={[
      { key: "title", label: "Title" }, { key: "cta_label", label: "CTA" },
      { key: "sort_order", label: "Order" }, { key: "is_active", label: "Active", render: (i) => i.is_active ? "Yes" : "No" },
    ]}
    fields={[
      { key: "title", label: "Title", full: true }, { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "cta_label", label: "CTA label" }, { key: "cta_link", label: "CTA link" },
      { key: "image_url", label: "Image URL", full: true },
      { key: "sort_order", label: "Order", type: "number" }, { key: "is_active", label: "Active", type: "checkbox" },
    ]}
  />;
}
